import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArchiveIcon, AlertCircleIcon } from 'lucide-react';
interface ArchiveEducatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  educatorName: string;
}
export const ArchiveEducatorModal: React.FC<ArchiveEducatorModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  educatorName
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(reason);
      setReason('');
      onClose();
    } catch (error) {
      console.error('Error archiving educator:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleClose = () => {
    if (!isSubmitting) {
      setReason('');
      onClose();
    }
  };
  return <AnimatePresence data-id="element-127">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-50" onClick={handleClose} data-id="element-128" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose} data-id="element-129">
            <div className="bg-surface-elevated rounded-2xl shadow-2xl w-full max-w-md border border-custom max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-130">
              {/* Header */}
              <div className="flex items-start justify-between p-4 sm:p-6 border-b border-custom" data-id="element-131">
                <div className="flex items-center gap-3" data-id="element-132">
                  <div className="w-10 h-10 rounded-full bg-[#FF9D33] bg-opacity-10 flex items-center justify-center flex-shrink-0" data-id="element-133">
                    <ArchiveIcon size={20} className="text-[#FF9D33]" data-id="element-134" />
                  </div>
                  <div className="min-w-0" data-id="element-135">
                    <h2 className="text-lg sm:text-xl font-bold text-text-primary" data-id="element-136">
                      Archive Educator
                    </h2>
                    <p className="text-xs sm:text-sm text-text-secondary mt-0.5 truncate" data-id="element-137">
                      {educatorName}
                    </p>
                  </div>
                </div>
                <button onClick={handleClose} disabled={isSubmitting} className="p-2 rounded-lg hover:bg-surface transition-colors disabled:opacity-50 flex-shrink-0" aria-label="Close modal" data-id="element-138">
                  <XIcon size={20} className="text-text-secondary" data-id="element-139" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 space-y-4" data-id="element-140">
                {/* Info Box */}
                <div className="bg-[#FF9D33] bg-opacity-5 border border-[#FF9D33] border-opacity-20 rounded-xl p-3" data-id="element-141">
                  <div className="flex gap-2" data-id="element-142">
                    <AlertCircleIcon size={16} className="text-[#FF9D33] flex-shrink-0 mt-0.5" data-id="element-143" />
                    <div className="text-xs leading-relaxed text-text-primary space-y-2" data-id="element-144">
                      <p className="font-semibold" data-id="element-145">
                        Changes status to "Archived"
                      </p>
                      <p className="text-text-secondary" data-id="element-146">
                        Remains organization member. All data preserved. Can
                        still log in to Ainstein. Reversible anytime.
                      </p>
                      <p className="text-text-tertiary text-[10px]" data-id="element-147">
                        Ideal for educators who left but used company email
                      </p>
                    </div>
                  </div>
                </div>

                {/* Reason Input */}
                <div data-id="element-148">
                  <label htmlFor="archive-reason" className="block text-sm font-semibold text-text-primary mb-2" data-id="element-149">
                    Reason{' '}
                    <span className="text-text-tertiary font-normal text-xs" data-id="element-150">
                      (optional)
                    </span>
                  </label>
                  <textarea id="archive-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g., Left organization, End of contract" disabled={isSubmitting} className="w-full px-3 py-2 bg-surface border-2 border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-[#00B75F] text-text-primary placeholder-text-tertiary resize-none disabled:opacity-50" rows={3} data-id="element-151" />
                  <p className="text-xs text-text-tertiary mt-1.5" data-id="element-152">
                    Recorded in audit log
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 sm:p-6 border-t border-custom bg-surface" data-id="element-153">
                <button onClick={handleClose} disabled={isSubmitting} className="px-4 py-2 border-2 border-custom text-text-primary rounded-lg font-semibold hover:bg-surface-elevated transition-colors disabled:opacity-50" data-id="element-154">
                  Cancel
                </button>
                <button onClick={handleConfirm} disabled={isSubmitting} className="px-4 py-2 bg-[#FF9D33] text-white rounded-lg font-semibold hover:bg-[#e68a2e] transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2" data-id="element-155">
                  {isSubmitting ? <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-156" />
                      Archiving...
                    </> : <>
                      <ArchiveIcon size={16} data-id="element-157" />
                      Archive
                    </>}
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};